// Environment configuration with TypeScript validation
// This ensures all required environment variables are present and typed correctly
// Test comment to trigger pre-commit formatting

export interface AppConfig {
  environment: 'development' | 'staging' | 'production';
  app: {
    name: string;
    url: string;
  };
  firebase: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
  };
  api: {
    baseUrl: string;
  };
  storage: {
    cdnUrl: string;
  };
  features: {
    analytics: boolean;
    devtools: boolean;
  };
  payments: {
    stripe: {
      publishableKey: string | null;
    };
  };
  logging: {
    level: 'debug' | 'info' | 'warn' | 'error';
  };
  // Development-specific emulator settings
  emulators?: {
    auth: {
      url: string;
    };
    firestore: {
      host: string;
      port: number;
    };
  };
}

// Validate and parse environment variables
function getRequiredEnvVar(key: string): string {
  const value = import.meta.env[key];
  if (!value) {
    throw new Error(`Missing required environment variable: ${key}`);
  }
  return value;
}

function getOptionalEnvVar(key: string, defaultValue: string): string {
  return import.meta.env[key] || defaultValue;
}

function getBooleanEnvVar(key: string, defaultValue: boolean): boolean {
  const value = import.meta.env[key];
  if (value === undefined) return defaultValue;
  return value === 'true';
}

// Create environment configuration with validation
export const appConfig: AppConfig = {
  environment: getRequiredEnvVar(
    'VITE_ENVIRONMENT'
  ) as AppConfig['environment'],

  app: {
    name: getRequiredEnvVar('VITE_APP_NAME'),
    url: getRequiredEnvVar('VITE_APP_URL'),
  },

  firebase: {
    apiKey: getRequiredEnvVar('VITE_FIREBASE_API_KEY'),
    authDomain: getRequiredEnvVar('VITE_FIREBASE_AUTH_DOMAIN'),
    projectId: getRequiredEnvVar('VITE_FIREBASE_PROJECT_ID'),
    storageBucket: getRequiredEnvVar('VITE_FIREBASE_STORAGE_BUCKET'),
    messagingSenderId: getRequiredEnvVar('VITE_FIREBASE_MESSAGING_SENDER_ID'),
    appId: getRequiredEnvVar('VITE_FIREBASE_APP_ID'),
  },

  api: {
    baseUrl: getRequiredEnvVar('VITE_API_BASE_URL'),
  },

  storage: {
    cdnUrl: getRequiredEnvVar('VITE_STORAGE_CDN_URL'),
  },

  features: {
    analytics: getBooleanEnvVar('VITE_ENABLE_ANALYTICS', false),
    devtools: getBooleanEnvVar('VITE_ENABLE_DEVTOOLS', false),
  },

  payments: {
    stripe: {
      publishableKey:
        getOptionalEnvVar('VITE_STRIPE_PUBLISHABLE_KEY', '') || null,
    },
  },

  logging: {
    level: getOptionalEnvVar(
      'VITE_LOG_LEVEL',
      'info'
    ) as AppConfig['logging']['level'],
  },
};

// Add emulator configuration for development
// Skip emulators if VITE_USE_LIVE_BACKEND is set (for E2E tests)
const useLiveBackend = getBooleanEnvVar('VITE_USE_LIVE_BACKEND', false);

if (appConfig.environment === 'development' && !useLiveBackend) {
  const authUrl = getOptionalEnvVar('VITE_FIREBASE_EMULATOR_AUTH_URL', '');
  const firestoreHost = getOptionalEnvVar(
    'VITE_FIREBASE_EMULATOR_FIRESTORE_HOST',
    ''
  );
  const firestorePort = getOptionalEnvVar(
    'VITE_FIREBASE_EMULATOR_FIRESTORE_PORT',
    '8080'
  );
  const apiUrl = getOptionalEnvVar('VITE_FIREBASE_EMULATOR_API_URL', '');

  if (authUrl && firestoreHost) {
    appConfig.emulators = {
      auth: { url: authUrl },
      firestore: {
        host: firestoreHost,
        port: parseInt(firestorePort, 10),
      },
    };

    // Use emulator API URL when emulators are configured
    if (apiUrl) {
      appConfig.api.baseUrl = apiUrl;
      console.log('🔧 Using Firebase Emulator API:', apiUrl);
    }
  }
}

// Environment-specific utilities
export const isDevelopment = appConfig.environment === 'development';
export const isStaging = appConfig.environment === 'staging';
export const isProduction = appConfig.environment === 'production';

// Export for debugging (only in development)
if (isDevelopment) {
  console.log('🔧 Environment Configuration:', appConfig);
}
